// Folder permissions belong to one account on this browser. Compound keys prevent
// another account on the same computer from reading or using a saved folder.
// The v1 `gameFolders` store had no owner: keep it untouched, but never adopt its
// handles automatically. Existing users must explicitly select their folders again.

import type { DirHandle } from "../setups/saveToFolder";

const DB_NAME = "pacecraft";
const STORE = "accountGameFolders";

function accountKey(userId: string, gameId: string): [string, string] {
  if (!userId) throw new Error("A game folder requires an account");
  return [userId, gameId];
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 2);
    request.onupgradeneeded = () => request.result.createObjectStore(STORE);
    request.onsuccess = () => {
      const db = request.result;
      db.onversionchange = () => db.close();
      resolve(db);
    };
    request.onerror = () => reject(request.error ?? new Error("Unable to open the game folder store"));
  });
}

function run<T>(mode: IDBTransactionMode, fn: (store: IDBObjectStore) => IDBRequest): Promise<T> {
  return openDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        let transaction: IDBTransaction;
        let request: IDBRequest;
        try {
          transaction = db.transaction(STORE, mode);
          request = fn(transaction.objectStore(STORE));
        } catch (cause) {
          db.close();
          reject(cause);
          return;
        }
        let settled = false;
        let result: T;
        transaction.oncomplete = () => {
          if (settled) return;
          settled = true;
          db.close();
          resolve(result);
        };
        const fail = (): void => {
          if (settled) return;
          settled = true;
          db.close();
          reject(transaction.error ?? request.error ?? new Error("Game folder transaction failed"));
        };
        transaction.onerror = fail;
        transaction.onabort = fail;
        request.onsuccess = () => {
          result = request.result as T;
        };
      }),
  );
}

/** The folder handle chosen for a game for this account on this machine, or null. */
export function getGameFolder(userId: string, gameId: string): Promise<DirHandle | null> {
  return run<DirHandle | undefined>("readonly", (s) => s.get(accountKey(userId, gameId))).then((h) => h ?? null);
}

/** Remember the folder chosen for a game. */
export function setGameFolder(userId: string, gameId: string, handle: DirHandle): Promise<void> {
  return run<IDBValidKey>("readwrite", (s) => s.put(handle, accountKey(userId, gameId))).then(() => undefined);
}

/** Forget a game's folder (the user removed the game). */
export function removeGameFolder(userId: string, gameId: string): Promise<void> {
  return run<undefined>("readwrite", (s) => s.delete(accountKey(userId, gameId))).then(() => undefined);
}

/** All games that currently have a folder configured for this account on this machine. */
export function listGameFolders(userId: string): Promise<Record<string, DirHandle>> {
  const range = IDBKeyRange.bound(accountKey(userId, ""), accountKey(userId, "\uffff"));
  return openDb().then(
    (db) =>
      new Promise<Record<string, DirHandle>>((resolve, reject) => {
        const out: Record<string, DirHandle> = {};
        const transaction = db.transaction(STORE, "readonly");
        const cursor = transaction.objectStore(STORE).openCursor(range);
        let settled = false;
        cursor.onsuccess = () => {
          const c = cursor.result;
          if (!c) return;
          out[(c.key as [string, string])[1]] = c.value as DirHandle;
          c.continue();
        };
        transaction.oncomplete = () => {
          if (settled) return;
          settled = true;
          db.close();
          resolve(out);
        };
        const fail = (): void => {
          if (settled) return;
          settled = true;
          db.close();
          reject(transaction.error ?? cursor.error ?? new Error("Unable to list game folders"));
        };
        transaction.onerror = fail;
        transaction.onabort = fail;
      }),
  );
}
